import React from 'react';

import type { Car } from '@/api/cars/types';
import type {
  CancellationPolicy,
  Discount as DiscountType,
  Insurance,
} from '@/api/reservations/types';
import mixpanelService from '@/core/clients/mixpanel';
import { getDailyInsuranceCost } from '@/core/utils';

type CarMeta = { numDays: number };

type UseCarDetailTrackingArgs = {
  car: Car | null;
  carMeta: CarMeta | undefined;
  insurance: Insurance | null;
  cancellationPolicy: CancellationPolicy | null;
  discount: DiscountType | null;
  deliveryModeEnabled: boolean;
  calculatedCost: number;
};

export function useCarDetailTracking({
  car,
  carMeta,
  insurance,
  cancellationPolicy,
  discount,
  deliveryModeEnabled,
  calculatedCost,
}: UseCarDetailTrackingArgs) {
  // Baseline for Price Delta. Mirrors calculatedCost until the first user
  // selection, then advances to the post-selection total on each tracked event.
  const prevTrackingTotalRef = React.useRef(0);
  const hasTrackedSelectionRef = React.useRef(false);
  // Delivery is deferred until the next car refetch surfaces the new
  // deliveryCost. Insurance and Booking Policy fire synchronously in their
  // click handlers, since reactive effects re-fire as totals cascade.
  const prevDeliveryModeRef = React.useRef<boolean | null>(null);
  const pendingDeliveryToggleRef = React.useRef(false);
  const deliveryCostAtToggleRef = React.useRef(0);

  const protectionChangesRef = React.useRef(0);
  const bookingPolicyChangesRef = React.useRef(0);
  const deliveryMethodChangesRef = React.useRef(0);

  React.useEffect(() => {
    if (hasTrackedSelectionRef.current) return;
    if (!car || !carMeta) return;
    prevTrackingTotalRef.current = calculatedCost;
  }, [calculatedCost, car, carMeta]);

  // Computes the post-click total inside the click handler, before state updates.
  const computeTotalWithOverrides = React.useCallback(
    (overrides: {
      insurance?: Insurance | null;
      cancellationPolicy?: CancellationPolicy | null;
    }) => {
      if (!car || !carMeta) return 0;
      const ins =
        overrides.insurance !== undefined ? overrides.insurance : insurance;
      const cp =
        overrides.cancellationPolicy !== undefined
          ? overrides.cancellationPolicy
          : cancellationPolicy;
      const numDays = carMeta.numDays;
      let cost = car.discountedDailyRate
        ? car.discountedDailyRate * numDays
        : car.dailyRate * numDays;
      if (ins) cost += getDailyInsuranceCost(ins, car.dailyRate) * numDays;
      if (cp) cost += (cp.percentage / 100) * car.dailyRate * numDays;
      if (car.deliveryCost > 0) cost += car.deliveryCost;
      if (discount) cost -= discount.amount;
      return cost;
    },
    [car, carMeta, insurance, cancellationPolicy, discount]
  );

  const trackInsuranceChange = React.useCallback(
    (option: Insurance) => {
      const isChange = !!insurance && insurance.id !== option.id;
      if (!isChange || !car || !carMeta) return;

      const newTotal = computeTotalWithOverrides({ insurance: option });
      const effectiveDailyRate = car.discountedDailyRate || car.dailyRate;

      protectionChangesRef.current += 1;
      hasTrackedSelectionRef.current = true;
      mixpanelService.track('Car Option Selected', {
        'Option Type': 'Protection',
        'Option Value': option.name,
        'Price Delta': newTotal - prevTrackingTotalRef.current,
        'Current Total': newTotal,
        'Daily Price': effectiveDailyRate,
      });
      prevTrackingTotalRef.current = newTotal;
    },
    [insurance, car, carMeta, computeTotalWithOverrides]
  );

  const trackCancellationPolicyChange = React.useCallback(
    (option: CancellationPolicy) => {
      const isChange =
        !!cancellationPolicy && cancellationPolicy.id !== option.id;
      if (!isChange || !car || !carMeta) return;

      const newTotal = computeTotalWithOverrides({
        cancellationPolicy: option,
      });
      const effectiveDailyRate = car.discountedDailyRate || car.dailyRate;

      bookingPolicyChangesRef.current += 1;
      hasTrackedSelectionRef.current = true;
      mixpanelService.track('Car Option Selected', {
        'Option Type': 'Booking Policy',
        'Option Value': option.title,
        'Price Delta': newTotal - prevTrackingTotalRef.current,
        'Current Total': newTotal,
        'Daily Price': effectiveDailyRate,
      });
      prevTrackingTotalRef.current = newTotal;
    },
    [cancellationPolicy, car, carMeta, computeTotalWithOverrides]
  );

  React.useEffect(() => {
    if (!car || !carMeta) return;
    const currentDeliveryCost = car.deliveryCost ?? 0;
    if (prevDeliveryModeRef.current === null) {
      prevDeliveryModeRef.current = deliveryModeEnabled;
      deliveryCostAtToggleRef.current = currentDeliveryCost;
      return;
    }
    if (
      !pendingDeliveryToggleRef.current &&
      prevDeliveryModeRef.current !== deliveryModeEnabled
    ) {
      pendingDeliveryToggleRef.current = true;
      deliveryCostAtToggleRef.current = currentDeliveryCost;
      return;
    }
    if (
      pendingDeliveryToggleRef.current &&
      currentDeliveryCost !== deliveryCostAtToggleRef.current
    ) {
      const effectiveDailyRate = car.discountedDailyRate || car.dailyRate;

      hasTrackedSelectionRef.current = true;
      mixpanelService.track('Car Option Selected', {
        'Option Type': 'Delivery Method',
        'Option Value': deliveryModeEnabled ? 'Delivery' : 'Pickup & Return',
        'Price Delta': calculatedCost - prevTrackingTotalRef.current,
        'Current Total': calculatedCost,
        'Daily Price': effectiveDailyRate,
      });
      prevDeliveryModeRef.current = deliveryModeEnabled;
      prevTrackingTotalRef.current = calculatedCost;
      deliveryCostAtToggleRef.current = currentDeliveryCost;
      pendingDeliveryToggleRef.current = false;
    }
  }, [deliveryModeEnabled, calculatedCost, car, carMeta]);

  // Counts every delivery toggle, including ones with no price change (no
  // addresses entered). Separate from the deferred Car Option Selected event.
  const initialDeliveryModeForCounterRef = React.useRef<boolean | null>(null);
  React.useEffect(() => {
    if (initialDeliveryModeForCounterRef.current === null) {
      initialDeliveryModeForCounterRef.current = deliveryModeEnabled;
      return;
    }
    if (initialDeliveryModeForCounterRef.current !== deliveryModeEnabled) {
      deliveryMethodChangesRef.current += 1;
      initialDeliveryModeForCounterRef.current = deliveryModeEnabled;
    }
  }, [deliveryModeEnabled]);

  const trackBookingCustomized = React.useCallback(() => {
    if (!car || !carMeta || !insurance || !cancellationPolicy) return;

    const numDays = carMeta.numDays;
    const effectiveDailyRate = car.discountedDailyRate || car.dailyRate;
    // Insurance is assessed against the full dailyRate, never the
    // discountedDailyRate (matches `calculatedCost`).
    const protectionDailyPrice = getDailyInsuranceCost(insurance, car.dailyRate);
    const bookingPolicyDailyPrice =
      car.dailyRate * (cancellationPolicy.percentage / 100);

    mixpanelService.track('Booking Customized', {
      Protection: insurance.name || insurance.category,
      'Booking Policy': cancellationPolicy.title,
      'Delivery Method': deliveryModeEnabled ? 'Delivery' : 'Pickup & Return',
      'Trip Days': numDays,
      'Protection Daily Price': protectionDailyPrice,
      'Booking Policy Daily Price': bookingPolicyDailyPrice,
      'Delivery Fee': car.deliveryCost ?? 0,
      'Discount Code Applied': !!discount,
      'Daily Price': effectiveDailyRate,
      'Total Price': calculatedCost,
      'Protection Changes': protectionChangesRef.current,
      'Booking Policy Changes': bookingPolicyChangesRef.current,
      'Delivery Method Changes': deliveryMethodChangesRef.current,
    });
  }, [
    car,
    carMeta,
    insurance,
    cancellationPolicy,
    discount,
    deliveryModeEnabled,
    calculatedCost,
  ]);

  return {
    trackInsuranceChange,
    trackCancellationPolicyChange,
    trackBookingCustomized,
  };
}
